import { routes } from "@/config/routes";
import useApi from "@/hooks/useApi";
import PushNotificationRepository from "@/utilities/repositories/PushNotification";
import {
  EPushNotificationStatus,
  ICreatePushNotificationDTO,
  IPushNotification,
  ISchedulePushNotificationDTO,
} from "@/utilities/types/PushNotification";
import { IResponseData } from "@/utilities/types/requests";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import PushNotificationForm from "./components/push-notification-form";

export default function CreateEditPushNotification() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState<IPushNotification | null>(
    null,
  );
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const { request: getNotification } = useApi<IResponseData<IPushNotification>>(
    {
      request: PushNotificationRepository.get,
    },
  );

  const { request: createDraft, loading: isCreating } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.create,
  });

  const { request: updateDraft, loading: isUpdating } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.updateDraft,
  });

  const { request: sendBlast, loading: isSending } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.send,
  });

  const { request: scheduleBlast, loading: isScheduling } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.schedule,
  });

  const { request: cancelNotification } = useApi<
    IResponseData<IPushNotification>
  >({
    request: PushNotificationRepository.cancel,
  });

  const isEditMode = Boolean(id) && id !== ":id";

  useEffect(() => {
    if (isEditMode && id) {
      fetchDetail(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDetail = async (notificationId: string) => {
    try {
      setIsLoadingDetail(true);
      const response = await getNotification(notificationId);
      if (response?.data) {
        setInitialData(response.data);
      }
    } catch (error) {
      console.error("Failed to load notification", error);
      toast.error("Failed to load notification");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Create a new draft. Does NOT navigate - used when the form is about to
  // send/schedule but no blast id exists yet.
  const handleCreateDraft = useCallback(
    async (data: ICreatePushNotificationDTO): Promise<IPushNotification> => {
      try {
        const res = await createDraft(data);
        if (res?.data) {
          return res.data;
        }
        throw new Error("Empty response");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to save draft";
        toast.error(message);
        throw error;
      }
    },
    [createDraft],
  );

  // Update an existing draft by id. Used when the form is in update mode and
  // the user clicks Send/Schedule to make sure the latest form values are
  // persisted before triggering the blast.
  const handleUpdateDraft = useCallback(
    async (
      notificationId: string,
      data: ICreatePushNotificationDTO,
    ): Promise<IPushNotification> => {
      try {
        const res = await updateDraft(notificationId, data);
        if (res?.data) {
          setInitialData(res.data);
          return res.data;
        }
        throw new Error("Empty response");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to save draft";
        toast.error(message);
        throw error;
      }
    },
    [updateDraft],
  );

  // Save-as-Draft button. Creates a new draft (and redirects to its edit page)
  // or updates the existing one in-place.
  const handleSaveDraft = useCallback(
    async (data: ICreatePushNotificationDTO): Promise<IPushNotification> => {
      if (isEditMode && id) {
        try {
          const res = await updateDraft(id, data);
          if (res?.data) {
            setInitialData(res.data);
            toast.success("Draft updated");
            return res.data;
          }
          throw new Error("Empty response");
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Failed to save draft";
          toast.error(message);
          throw error;
        }
      }
      try {
        const res = await createDraft(data);
        if (res?.data) {
          toast.success("Draft saved");
          // Redirect to edit page so user can send the saved draft.
          navigate(routes.pushNotification.edit(res.data.id), {
            replace: true,
          });
          return res.data;
        }
        throw new Error("Empty response");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to save draft";
        toast.error(message);
        throw error;
      }
    },
    [createDraft, updateDraft, id, isEditMode, navigate],
  );

  // Ensure a draft exists, then call /preview. If editing an existing draft,
  // updates it first so the preview reflects the current form values.
  const handleSendNow = useCallback(
    async (blastId: string): Promise<void> => {
      try {
        await sendBlast(blastId);
        toast.success("Push blast queued for sending");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to send";
        toast.error(message);
        throw error;
      }
    },
    [sendBlast],
  );

  const handleSchedule = useCallback(
    async (blastId: string, scheduledAt: Date): Promise<void> => {
      try {
        const payload: ISchedulePushNotificationDTO = {
          scheduledAt: scheduledAt.toISOString(),
        };
        await scheduleBlast(blastId, payload);
        toast.success("Push blast scheduled");
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to schedule";
        toast.error(message);
        throw error;
      }
    },
    [scheduleBlast],
  );

  const handleCancelScheduled = useCallback(
    async (notificationId: string) => {
      try {
        await cancelNotification(notificationId);
        toast.success("Scheduled notification cancelled");
        navigate(routes.pushNotification.listing);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to cancel";
        toast.error(message);
      }
    },
    [cancelNotification, navigate],
  );

  const isLocked =
    initialData?.status === EPushNotificationStatus.SENT ||
    initialData?.status === EPushNotificationStatus.SENDING ||
    initialData?.status === EPushNotificationStatus.FAILED ||
    initialData?.status === EPushNotificationStatus.CANCELLED;

  return (
    <div className="@container">
      <PushNotificationForm
        initialData={initialData}
        isLoading={isLoadingDetail}
        isSaving={isCreating || isUpdating || isSending || isScheduling}
        onCreateDraft={handleCreateDraft}
        onUpdateDraft={handleUpdateDraft}
        onSaveDraft={handleSaveDraft}
        onSendNow={handleSendNow}
        onSchedule={handleSchedule}
        isUpdate={isEditMode}
        onCancelScheduled={
          initialData?.status === EPushNotificationStatus.SCHEDULED
            ? handleCancelScheduled
            : undefined
        }
      />
      {isLocked && (
        <p className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
          This notification is read-only because its current status does not
          allow edits.
        </p>
      )}
    </div>
  );
}
